import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import {
    Fade,
    Grid,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getDataFull, deleteData, postData } from '../../services/ajax/';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import MaterialTable, { DataType } from '../../components/material-table';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PeopleIcon from '@mui/icons-material/People';
import { useSnackbar } from 'notistack';
import VerLogDialog from '../../components/modal/log/verLog';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';
import { blue, orange, red, teal } from '@mui/material/colors';
import ListadoOperativoBanner from '../../components/listados/ListadoOperativoBanner';
import ProcessGuide from '../../components/orden/ProcessGuide';

const CHIP_DEFS = [
    { key: 'todos', label: 'Todos', color: 'primary' },
    { key: 'administradores', label: 'Administradores', color: 'warning' },
    { key: 'operarios', label: 'Operarios', color: 'info' },
];

const DEFAULT_NOTIF_TITULO = 'Notificación de prueba';
const DEFAULT_NOTIF_CUERPO = 'Mensaje de prueba enviado desde la administración de usuarios.';

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [referenciaVerLogModal, setReferenciaVerLogModal] = useState(null);
    const [idReferenciaVerLogModal] = useState(null);
    const [selectedChip, setSelectedChip] = useState('todos');
    const [counts, setCounts] = useState({});
    const [rowsTableMain, setRowsTableMain] = useState(null);
    const [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const [notifDestino, setNotifDestino] = useState(null);
    const [notifTitulo, setNotifTitulo] = useState(DEFAULT_NOTIF_TITULO);
    const [notifCuerpo, setNotifCuerpo] = useState(DEFAULT_NOTIF_CUERPO);

    const puedeGestionar = usuario?.nivel_usuario === 1 || usuario?.sup_usuarios;

    const chipItems = useMemo(
        () => CHIP_DEFS.map((c) => ({ ...c, count: counts[c.key] != null ? counts[c.key] : 0 })),
        [counts]
    );

    const onChipChange = useCallback((key) => {
        setSelectedChip(key);
        setTableMainNeedUpdate(Date.now());
    }, []);

    const onNuevo = useCallback(() => {
        globalsContext.setIdEdit(false);
        navigate('/usuario/nuevo');
    }, [globalsContext, navigate]);

    const onEditar = useCallback((row) => {
        globalsContext.setIdEdit(row.id_usuario);
        navigate('/usuario/editar');
    }, [globalsContext, navigate]);

    const onEliminar = useCallback((row) => {
        alertModal.showModal(
            'Eliminar usuario',
            'El usuario ' + (row.nombre_apellido || row.nombre) + ' será eliminado. ¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const response = await deleteData(`usuarios/${row.id_usuario}`, usuario.token);
                if (response.error === '') {
                    enqueueSnackbar('Usuario eliminado ok', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
                loadingModal.setOpen(false);
            }
        );
    }, [usuario.token, alertModal, loadingModal, enqueueSnackbar]);

    const onReestablecerClave = useCallback((row) => {
        alertModal.showModal(
            'Reestablecer contraseña',
            'Se enviará un email a ' + (row.email || row.nombre_apellido) + ' con un enlace para reestablecer su contraseña. ¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const response = await postData('usuarios/recover', {
                    email: row.email,
                    cuil: row.cuil || row.cuitCuil,
                });
                if (response.error === '') {
                    enqueueSnackbar('Se ha enviado un email con el enlace para reestablecer la contraseña', { variant: 'success' });
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
                loadingModal.setOpen(false);
            }
        );
    }, [alertModal, loadingModal, enqueueSnackbar]);

    const onAbrirNotifPrueba = useCallback((row) => {
        setNotifDestino(row);
        setNotifTitulo(DEFAULT_NOTIF_TITULO);
        setNotifCuerpo(DEFAULT_NOTIF_CUERPO);
    }, []);

    const onCerrarNotifPrueba = useCallback(() => {
        setNotifDestino(null);
    }, []);

    const onEnviarNotifPrueba = useCallback(async () => {
        if (!notifDestino) return;
        const titulo = notifTitulo.trim();
        const cuerpo = notifCuerpo.trim();
        if (!titulo || !cuerpo) return;

        loadingModal.setOpen(true);
        const response = await postData(
            `usuarios/${notifDestino.id_usuario}/notificacion-prueba`,
            { titulo, cuerpo },
            usuario.token
        );
        if (response.error === '') {
            enqueueSnackbar('La notificación de prueba fue enviada.', { variant: 'success' });
            setNotifDestino(null);
        } else {
            enqueueSnackbar(response.error, { variant: 'error' });
        }
        loadingModal.setOpen(false);
    }, [notifDestino, notifTitulo, notifCuerpo, loadingModal, usuario.token, enqueueSnackbar]);

    const tableMain = useMemo(() => ({
        title: 'Usuarios',
        idName: 'id_usuario',
        defaultOrder: 'asc',
        headers: [
            { id: 'nombre_apellido', label: 'Nombre', dataType: DataType.TEXT, sortable: true },
            { id: 'email', label: 'Email', dataType: DataType.TEXT, sortable: true },
            { id: 'cuil', label: 'CUIT/CUIL', dataType: DataType.CUIL, sortable: true },
            {
                id: 'nivel_usuario',
                label: 'Nivel',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => (
                    <Chip
                        size="small"
                        color={row.nivel_usuario === 1 ? 'warning' : 'info'}
                        label={row.nivel_usuario === 1 ? 'Administrador' : 'Operario'}
                    />
                ),
            },
            {
                id: 'establecimiento_nombre',
                label: 'Establecimiento',
                dataType: DataType.TEXT,
                sortable: false,
                customRender: (row) => row.establecimiento_nombre || (row.nivel_usuario === 1 ? '—' : 'Sin asignar'),
            },
            {
                id: 'sup_usuarios',
                label: 'Sup. usuarios',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => (
                    <Chip size="small" variant="outlined" label={row.sup_usuarios ? 'Sí' : 'No'} />
                ),
            },
        ],
        loadData: async (orderBy, order, page, rowsPerPage, searchText, _sf, pageUpdate, totalUpdate, filterListUpdate) => {
            setRowsTableMain(null);
            const params = new URLSearchParams();
            params.append('per_page', String(rowsPerPage));
            params.append('page', String(page + 1));
            if (orderBy) params.append('order_by', orderBy);
            if (order) params.append('order', order);
            if (searchText) params.append('search_text', searchText);
            if (selectedChip && selectedChip !== 'todos') params.append('chip', selectedChip);

            const response = await getDataFull(`usuarios?${params.toString()}`, usuario.token);
            if (response.error === '') {
                pageUpdate(response.meta?.page ? response.meta.page - 1 : page);
                totalUpdate(response.meta?.total || 0);
                if (filterListUpdate) filterListUpdate([]);
                if (response.counts) setCounts(response.counts);
                setRowsTableMain(response.data || []);
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        },
        moreMenuItems: [],
        rowActions: (row) => {
            if (!puedeGestionar) return [];
            return [
                {
                    label: 'Modificar datos',
                    secondary: 'Cambiar nombre, email, nivel o establecimiento',
                    icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                    itemColor: blue[600],
                    action: () => onEditar(row),
                },
                {
                    label: 'Enviar enlace para cambiar contraseña',
                    secondary: 'Se enviará un correo con un enlace seguro',
                    icon: <LockResetRoundedIcon sx={{ color: orange[700] }} />,
                    itemColor: orange[700],
                    action: () => onReestablecerClave(row),
                },
                {
                    label: 'Enviar notificación de prueba',
                    secondary: 'Permite definir título y mensaje; aparece en la campana del destinatario',
                    icon: <NotificationsActiveRoundedIcon sx={{ color: teal[600] }} />,
                    itemColor: teal[600],
                    action: () => onAbrirNotifPrueba(row),
                },
                {
                    label: 'Eliminar usuario',
                    secondary: 'Se borrará de forma permanente',
                    icon: <DeleteRoundedIcon sx={{ color: red[600] }} />,
                    itemColor: red[600],
                    dividerBefore: true,
                    action: () => onEliminar(row),
                },
            ];
        },
    }), [usuario.token, selectedChip, puedeGestionar, onEditar, onEliminar, onReestablecerClave, onAbrirNotifPrueba, enqueueSnackbar]);

    const puedeEnviarNotif = Boolean(notifTitulo.trim() && notifCuerpo.trim());

    return (
        <Layout nombrePagina="Usuarios">
            <Fade in timeout={900}>
                <Grid size={12}>
                    <ListadoOperativoBanner
                        title="Usuarios"
                        description="Administra accesos al sistema. Los operarios deben estar vinculados a un establecimiento; los administradores gestionan el dominio completo."
                        icon={PeopleIcon}
                        accentColor="warning"
                        headerAction={puedeGestionar ? (
                            <Button startIcon={<AddCircleOutlineRoundedIcon />} onClick={onNuevo}>
                                Nuevo usuario
                            </Button>
                        ) : null}
                        chips={chipItems}
                        selectedChip={selectedChip}
                        onChipChange={onChipChange}
                    />
                    <ProcessGuide
                        title="Guía rápida"
                        steps={[
                            'Administrador: acceso total. Operario: requiere establecimiento público o privado.',
                            'El tipo de establecimiento define si el operario puede cargar órdenes o solo responderlas.',
                            'Use el filtro superior para separar administradores y operarios.',
                        ]}
                    />
                    <MaterialTable
                        title={tableMain.title}
                        loadData={tableMain.loadData}
                        headers={tableMain.headers}
                        data={rowsTableMain}
                        moreMenuItems={tableMain.moreMenuItems}
                        selectable={false}
                        rowActions={tableMain.rowActions}
                        idName={tableMain.idName}
                        defaultOrder={tableMain.defaultOrder}
                        defaultRowsPerPage={10}
                        needUpdate={tableMainNeedUpdate}
                        hideFilters
                        onRowClick={puedeGestionar ? (row) => onEditar(row) : undefined}
                    />
                </Grid>
            </Fade>

            <Dialog open={Boolean(notifDestino)} onClose={onCerrarNotifPrueba} fullWidth maxWidth="sm">
                <DialogTitle>Notificación de prueba</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Destinatario:{' '}
                        {[notifDestino?.nombre_apellido, notifDestino?.email].filter(Boolean).join(' · ') || '—'}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Título"
                        fullWidth
                        value={notifTitulo}
                        onChange={(e) => setNotifTitulo(e.target.value)}
                        slotProps={{ htmlInput: { maxLength: 255 } }}
                        helperText="Máximo 255 caracteres."
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Mensaje"
                        fullWidth
                        multiline
                        minRows={3}
                        value={notifCuerpo}
                        onChange={(e) => setNotifCuerpo(e.target.value)}
                        slotProps={{ htmlInput: { maxLength: 2000 } }}
                        helperText="Máximo 2000 caracteres. La alerta del sistema se muestra si está activa y la pestaña del destinatario pasa a segundo plano."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCerrarNotifPrueba}>Cancelar</Button>
                    <Button
                        onClick={onEnviarNotifPrueba}
                        variant="contained"
                        color="secondary"
                        disabled={!puedeEnviarNotif}
                    >
                        Enviar
                    </Button>
                </DialogActions>
            </Dialog>

            <VerLogDialog
                referencia={referenciaVerLogModal}
                setReferencia={setReferenciaVerLogModal}
                id_referencia={idReferenciaVerLogModal}
                id_usuario={usuario.id_usuario}
            />
        </Layout>
    );
}

export default Page;
